import { supabase } from '@/integrations/supabase/client';
import { Profile, SocialMedia, ProfileUpdate, SocialMediaUpdate } from '@/types/playerProfile';

export async function fetchPlayerProfile(userId: string) {
  const { data: profileData, error: profileError } = await supabase
    .from('profiles')
    .select('id, nickname, preferred_game')
    .eq('id', userId)
    .single();
    
  if (profileError) {
    console.error('Error fetching profile:', profileError);
    throw profileError;
  }
  
  return profileData as Profile;
}

export async function fetchSocialMedia(userId: string) {
  try {
    console.log('🔍 Buscando dados de social media para usuário:', userId);
    
    // Buscar registros - pode haver múltiplos, vamos pegar o primeiro
    console.log('📖 Tentando buscar registro existente de social media');
    const { data: socialDataArray, error: socialError } = await supabase
      .from('player_social_media')
      .select('id, biography, instagram_url, discord_url, twitch_url, youtube_url, image_url, cover_url')
      .eq('profile_id', userId)
      .order('created_at', { ascending: false }) // Pega o mais recente
      .limit(1);
  
    if (socialError) {
      console.error('❌ Erro ao buscar social media (tabela pode não existir):', socialError);
      
      // Se a tabela não existe ou há problema de permissão, retorna dados padrão
      console.log('🔄 Retornando dados padrão devido a problema na tabela');
      return {
        id: '',
        biography: '',
        instagram_url: '',
        discord_url: '',
        twitch_url: '',
        youtube_url: '',
        image_url: '',
        cover_url: ''
      } as SocialMedia;
    }
    
    // Se não há dados ou array está vazio
    if (!socialDataArray || socialDataArray.length === 0) {
      // Record doesn't exist, try to create one
      console.log('📝 Registro não encontrado, tentando criar novo registro de social media para perfil:', userId);
      
      const { data: newSocial, error: createError } = await supabase
        .from('player_social_media')
        .insert({ profile_id: userId })
        .select('id, biography, instagram_url, discord_url, twitch_url, youtube_url, image_url, cover_url')
        .maybeSingle();
        
      if (createError) {
        console.error('❌ Erro ao criar social media, retornando dados padrão:', createError);
        // Return default data if can't create
        return {
          id: '',
          biography: '',
          instagram_url: '',
          discord_url: '',
          twitch_url: '',
          youtube_url: '',
          image_url: '',
          cover_url: ''
        } as SocialMedia;
      }
      
      console.log('✅ Registro de social media criado com sucesso');
      return newSocial as SocialMedia;
    }
    
    // Pega o primeiro registro se existir
    const socialData = socialDataArray[0];
    
    // Se existem múltiplos registros, vamos limpar os duplicados
    if (socialDataArray.length > 1) {
      console.log('⚠️ Múltiplos registros encontrados, limpando duplicados...');
      
      // Manter apenas o primeiro (mais recente) e deletar os outros
      const idsToDelete = socialDataArray.slice(1).map(record => record.id);
      if (idsToDelete.length > 0) {
        await supabase
          .from('player_social_media')
          .delete()
          .in('id', idsToDelete);
        console.log('🧹 Registros duplicados removidos');
      }
    }
    
    console.log('✅ Dados de social media encontrados com sucesso');
    return socialData as SocialMedia;
    
  } catch (error) {
    console.error('❌ Erro geral em fetchSocialMedia, retornando dados padrão:', error);
    // Always return something usable instead of throwing
    return {
      id: '',
      biography: '',
      instagram_url: '',
      discord_url: '',
      twitch_url: '',
      youtube_url: '',
      image_url: '',
      cover_url: ''
    } as SocialMedia;
  }
}

export async function updatePlayerProfile(userId: string, profileUpdate: ProfileUpdate) {
  const { error } = await supabase
    .from('profiles')
    .update(profileUpdate)
    .eq('id', userId);
    
  if (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
  
  return true;
}

export async function updateSocialMedia(userId: string, socialUpdate: SocialMediaUpdate, existingSocialMedia: SocialMedia | null) {
  if (!existingSocialMedia) {
    // Create a new social media record
    const { data, error } = await supabase
      .from('player_social_media')
      .insert({ 
        profile_id: userId,
        ...socialUpdate 
      })
      .select()
      .single();
      
    if (error) {
      console.error('Error creating social media:', error);
      throw error;
    }
    
    return data as SocialMedia;
  } else {
    // Update existing record
    const { error } = await supabase
      .from('player_social_media')
      .update(socialUpdate)
      .eq('profile_id', userId);
      
    if (error) {
      console.error('Error updating social media:', error);
      throw error;
    }
    
    return { ...existingSocialMedia, ...socialUpdate } as SocialMedia;
  }
}

export async function uploadProfileImage(userId: string, file: File) {
  try {
    console.log('🔄 Iniciando upload de imagem para usuário:', userId);
    console.log('📱 User agent:', navigator.userAgent);
    console.log('📁 Arquivo selecionado:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });
    
    // Detectar se é mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    console.log('📱 Dispositivo mobile detectado:', isMobile);
    
    // Validar arquivo
    if (!file.type.startsWith('image/')) {
      throw new Error('Por favor, selecione um arquivo de imagem válido.');
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB
      throw new Error('A imagem deve ter no máximo 5MB.');
    }
    
    // Verificar se o arquivo não está corrompido
    if (file.size === 0) {
      throw new Error('O arquivo selecionado está vazio ou corrompido.');
    }
    
    // Generate a unique filename
    const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const timestamp = Date.now();
    const fileName = `${userId}/${timestamp}_mobile.${fileExt}`;
    
    console.log('📁 Nome do arquivo gerado:', fileName);
    
    // Lista dos buckets possíveis para tentar
    const possibleBuckets = ['profile-images', 'avatars', 'images', 'uploads'];
    let workingBucket = 'profile-images'; // default bucket
    
    // Verificar se algum bucket existe
    console.log('🔍 Verificando buckets disponíveis...');
    
    try {
      const { data: buckets, error: bucketsError } = await supabase.storage.listBuckets();
      
      if (!bucketsError && buckets) {
        console.log('📋 Buckets encontrados:', buckets?.map(b => ({ name: b.name, public: b.public })));
        
        // Procura por um bucket que já existe
        for (const bucketName of possibleBuckets) {
          const existingBucket = buckets?.find(bucket => bucket.name === bucketName);
          if (existingBucket) {
            workingBucket = bucketName;
            console.log('✅ Usando bucket existente:', bucketName);
            break;
          }
        }
      } else {
        console.error('❌ Erro ao listar buckets:', bucketsError);
      }
    } catch (bucketsListError) {
      console.error('❌ Erro crítico ao listar buckets:', bucketsListError);
    }
    
    // Tentar fazer o upload
    console.log('🚀 Tentando upload no bucket:', workingBucket);
    
    let uploadSuccess = false;
    let finalImageUrl = '';
    let lastError = null;
    
    // Configurações específicas para mobile
    const uploadOptions = {
      cacheControl: '3600',
      upsert: true,
      ...(isMobile && {
        // Configurações adicionais para mobile se necessário
        timeout: 30000 // 30 segundos timeout para mobile
      })
    };
    
    // Tentar upload em cada bucket até conseguir
    for (const bucketName of [workingBucket, ...possibleBuckets.filter(b => b !== workingBucket)]) {
      if (uploadSuccess) break;
      
      try {
        console.log('🔄 Tentando upload no bucket:', bucketName);
        
        const { data: uploadResult, error: uploadError } = await supabase.storage
          .from(bucketName)
          .upload(fileName, file, uploadOptions);
        
        if (uploadError) {
          console.log('❌ Falha no bucket:', bucketName, uploadError?.message);
          lastError = uploadError;
          continue;
        }
        
        if (uploadResult) {
          console.log('✅ Upload bem-sucedido no bucket:', bucketName);
          
          // Obter URL pública
          const { data: publicUrlData } = supabase.storage
            .from(bucketName)
            .getPublicUrl(fileName);
          
          if (publicUrlData?.publicUrl) {
            finalImageUrl = publicUrlData.publicUrl;
            uploadSuccess = true;
            console.log('✅ URL pública gerada:', finalImageUrl);
            break;
          } else {
            console.log('❌ Falha ao gerar URL pública para bucket:', bucketName);
          }
        }
      } catch (bucketError) {
        console.log('❌ Erro no bucket:', bucketName, bucketError);
        lastError = bucketError;
      }
    }
    
    if (!uploadSuccess || !finalImageUrl) {
      const errorMessage = isMobile 
        ? 'Problema ao enviar foto do celular. Verifique sua conexão e tente novamente.'
        : 'Não foi possível fazer upload da imagem em nenhum bucket disponível.';
      
      console.error('❌ Upload falhou em todos os buckets. Último erro:', lastError);
      throw new Error(errorMessage);
    }
    
    console.log('🎉 Upload concluído com sucesso! URL:', finalImageUrl);
    return finalImageUrl;
    
  } catch (error) {
    console.error('❌ Erro geral no upload:', error);
    
    // Mensagens de erro específicas para mobile
    if (error instanceof Error) {
      if (error.message.includes('network') || error.message.includes('fetch')) {
        throw new Error('Problema de conexão. Verifique sua internet e tente novamente.');
      } else if (error.message.includes('timeout')) {
        throw new Error('Upload demorou muito para completar. Tente com uma imagem menor.');
      } else if (error.message.includes('permission') || error.message.includes('policy')) {
        throw new Error('Problema de permissão no sistema. Entre em contato com o suporte.');
      }
    }
    
    throw error;
  }
}
